import axios from 'axios';
import { expressURL, getAuthHeaders } from './networkUtils';

export type PushConfig = {
  enabled: boolean;
  publicKey: string | null;
};

export type PushSupport = {
  supported: boolean;
  reason?: string;
};

export type PushSubscriptionJson = {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
};

const SW_PATH = '/service-worker.js';
const SW_SCOPE = '/';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export function getPushSupport(): PushSupport {
  if (typeof window === 'undefined') {
    return { supported: false, reason: 'Not in a browser' };
  }
  if (!('serviceWorker' in navigator)) {
    return { supported: false, reason: 'Service workers are not supported in this browser' };
  }
  if (!('PushManager' in window)) {
    return { supported: false, reason: 'Push notifications are not supported in this browser' };
  }
  if (!('Notification' in window)) {
    return { supported: false, reason: 'Notifications are not supported in this browser' };
  }
  return { supported: true };
}

export async function getPushServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  const support = getPushSupport();
  if (!support.supported) return null;
  try {
    const scoped = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    if (scoped) return scoped;
    return await navigator.serviceWorker.getRegistration(SW_PATH);
  } catch (_) {
    return null;
  }
}

export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  const support = getPushSupport();
  if (!support.supported) return null;
  try {
    return await navigator.serviceWorker.register(SW_PATH, { scope: SW_SCOPE });
  } catch (error) {
    console.warn('Service worker registration failed:', error);
    return null;
  }
}

export async function fetchPushConfig(): Promise<PushConfig> {
  const response = await axios.get(`${expressURL()}/api/push/config`, getAuthHeaders());
  return response.data as PushConfig;
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function getBrowserPushSubscription(
  registration: ServiceWorkerRegistration
): Promise<PushSubscription | null> {
  try {
    return await registration.pushManager.getSubscription();
  } catch (_) {
    return null;
  }
}

export async function isBrowserPushSubscribed(): Promise<boolean> {
  const registration = await getPushServiceWorkerRegistration();
  if (!registration) return false;
  const subscription = await getBrowserPushSubscription(registration);
  return !!subscription;
}

export function subscriptionToJson(subscription: PushSubscription): PushSubscriptionJson | null {
  const json = subscription.toJSON();
  const endpoint = json.endpoint;
  const p256dh = json.keys && json.keys.p256dh;
  const auth = json.keys && json.keys.auth;
  if (!endpoint || !p256dh || !auth) return null;
  return { endpoint, keys: { p256dh, auth } };
}

export async function subscribeToPush(publicKey: string): Promise<PushSubscriptionJson> {
  const registration = await registerPushServiceWorker();
  if (!registration) {
    throw new Error('Could not register the notification service worker');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted');
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
  }

  const payload = subscriptionToJson(subscription);
  if (!payload) {
    throw new Error('Could not read push subscription from the browser');
  }

  await axios.post(`${expressURL()}/api/push/subscribe`, payload, getAuthHeaders());
  return payload;
}

export async function unsubscribeFromPush(): Promise<void> {
  const support = getPushSupport();
  if (!support.supported) return;

  const registration = await getPushServiceWorkerRegistration();
  const subscription = registration
    ? await getBrowserPushSubscription(registration)
    : null;

  if (!subscription) {
    return;
  }

  const payload = subscriptionToJson(subscription);
  let serverError: Error | null = null;

  if (payload) {
    try {
      await axios.post(
        `${expressURL()}/api/push/unsubscribe`,
        { endpoint: payload.endpoint },
        getAuthHeaders()
      );
    } catch (error) {
      serverError = error instanceof Error
        ? error
        : new Error('Could not remove push subscription from the server');
    }
  }

  try {
    await subscription.unsubscribe();
  } catch (_) {
    /* local cleanup is best-effort */
  }

  if (serverError) {
    throw serverError;
  }
}

export function isIosBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    && !(window as Window & { MSStream?: unknown }).MSStream;
}

export function isStandaloneDisplayMode(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
